import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { scheduleApi, dealsApi, adminApi, userApi } from '../services/api';
import { Map } from '../components/Map';

function BarChart({ values, dates }: { values: number[]; dates: string[] }) {
  const max = Math.max(...values, 1);
  const n = values.length;

  return (
    <div>
      <svg viewBox={`0 0 ${n * 10} 50`} className="w-full h-20" preserveAspectRatio="none">
        {values.map((v, i) => {
          const h = Math.max((v / max) * 46, v > 0 ? 2 : 0);
          return (
            <rect
              key={i}
              x={i * 10 + 0.5}
              y={50 - h}
              width={9}
              height={h}
              fill={v > 0 ? '#3b82f6' : '#f3f4f6'}
              rx="1"
            />
          );
        })}
      </svg>
      <div className="flex justify-between text-xs text-gray-400 mt-1">
        <span>{new Date(dates[0] + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
        <span>{new Date(dates[Math.floor(dates.length / 2)] + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
        <span>{new Date(dates[dates.length - 1] + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
      </div>
    </div>
  );
}

export function Home() {
  const today = new Date().toISOString().split('T')[0];
  const in10Days = new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const { data: currentUser } = useQuery({
    queryKey: ['user'],
    queryFn: () => userApi.get(),
  });

  const { data: todayVisits = [] } = useQuery({
    queryKey: ['schedule', today, today, 'planned'],
    queryFn: () => scheduleApi.list(today, today, 'planned'),
  });

  const { data: allDeals = [] } = useQuery({
    queryKey: ['deals'],
    queryFn: () => dealsApi.list({ activeOnly: true }),
  });

  const { data: activity } = useQuery({
    queryKey: ['admin', 'activity', 30],
    queryFn: () => adminApi.getActivity(30),
    enabled: !!currentUser?.is_admin,
  });

  const expiringDeals = allDeals
    .filter((d) => d.end_date <= in10Days && d.status === 'active')
    .sort((a, b) => a.end_date.localeCompare(b.end_date));

  const todayMarkers = todayVisits.map((v) => ({
    id: v.store.id,
    lat: v.store.lat,
    lng: v.store.lng,
    name: v.store.name,
    address: v.store.address_line1
      ? `${v.store.address_line1}${v.store.city ? `, ${v.store.city}` : ''}`
      : undefined,
    selected: false,
  }));

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const dayLabel = new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          {greeting()}{currentUser?.name ? `, ${currentUser.name}` : ''}!
        </h2>
        <p className="text-gray-500 mt-0.5">{dayLabel}</p>
      </div>

      {/* 3 Widgets */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* Today's Visits */}
        <div className="bg-white rounded-lg shadow p-4 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Today's Visits</h3>
            <Link to="/schedule" className="text-xs text-blue-600 hover:underline">Schedule →</Link>
          </div>
          {todayVisits.length === 0 ? (
            <div className="flex-1 flex items-center justify-center py-6">
              <p className="text-sm text-gray-400 italic">No visits scheduled today</p>
            </div>
          ) : (
            <div className="space-y-1 divide-y divide-gray-100">
              {todayVisits.map((v) => (
                <div key={v.id} className="flex items-start gap-3 py-2 first:pt-0">
                  <span className="shrink-0 text-xs text-gray-400 w-14 pt-0.5">
                    {new Date(v.start_datetime).toLocaleTimeString('en-US', {
                      hour: 'numeric', minute: '2-digit',
                    })}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{v.store.name}</p>
                    <p className="text-xs text-gray-400">{v.duration_minutes} min</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Mini Map — today's route */}
        <div className="bg-white rounded-lg shadow overflow-hidden flex flex-col">
          <div className="px-4 pt-4 pb-2 flex items-center justify-between shrink-0">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Today's Route</h3>
            {todayVisits.length > 0 && (
              <Link to={`/route/${today}`} className="text-xs text-blue-600 hover:underline">Full route →</Link>
            )}
          </div>
          <div className="flex-1 min-h-[180px]">
            {todayMarkers.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-gray-400 italic">
                No stops today
              </div>
            ) : (
              <Map markers={todayMarkers} zoom={11} />
            )}
          </div>
        </div>

        {/* Deals ending soon */}
        <div className="bg-white rounded-lg shadow p-4 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Deals Ending Soon</h3>
            <Link to="/deals" className="text-xs text-blue-600 hover:underline">All deals →</Link>
          </div>
          {expiringDeals.length === 0 ? (
            <div className="flex-1 flex items-center justify-center py-6">
              <p className="text-sm text-gray-400 italic">No deals ending within 10 days</p>
            </div>
          ) : (
            <div className="space-y-1 divide-y divide-gray-100">
              {expiringDeals.map((deal) => {
                const daysLeft = Math.ceil(
                  (new Date(deal.end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
                );
                return (
                  <div key={deal.id} className="py-2 first:pt-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{deal.name}</p>
                    <p className={`text-xs mt-0.5 font-medium ${daysLeft <= 3 ? 'text-red-500' : 'text-amber-500'}`}>
                      Ends in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Admin: User Activity Graph */}
      {currentUser?.is_admin && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
            User Activity — Last 30 Days
          </h3>

          {!activity ? (
            <p className="text-sm text-gray-400 text-center py-4">Loading activity...</p>
          ) : (
            <>
              <BarChart values={activity.daily_totals} dates={activity.dates} />

              {activity.by_user.length > 0 && (
                <div className="mt-5 border-t border-gray-100 pt-4 space-y-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Ranked by visits</p>
                  {activity.by_user.map((u, i) => {
                    const pct = activity.by_user[0].total_visits > 0
                      ? (u.total_visits / activity.by_user[0].total_visits) * 100
                      : 0;
                    return (
                      <div key={u.user_id} className="flex items-center gap-3">
                        <span className="text-xs text-gray-300 w-4 shrink-0">#{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-gray-800 truncate">{u.email}</span>
                            <span className="text-xs text-gray-400 ml-2 shrink-0">{u.total_visits}</span>
                          </div>
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-blue-400 rounded-full transition-all"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {activity.by_user.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4 mt-2">No activity recorded yet.</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
